//Azure AI Agent Service - Enhanced with Context Management
/**
 * Azure AI Service for communicating with the Azure AI backend
 * Enhanced with session-based context management to optimize token usage
 * 
 * Environment Variables:
 * - AZURE_AI_BACKEND_URL: The backend URL for the Azure AI service
 *   - Development default: 'https://aibackenddev.zavior.app/api/v1/chat'
 *   - Production: Required - will throw error if not set
 * 
 * Features:
 * - Session-based context management
 * - Token optimization for follow-up messages
 * - Performance tracking and analytics
 * - Smart context loading with user feedback
 * 
 * Usage:
 * - Set AZURE_AI_BACKEND_URL in your .env file
 * - In production, ensure this variable is properly configured
 */

import { 
  aiContextManager, 
  createAISession, 
  buildAIPayload, 
  updateAISessionState, 
  getAISessionAnalytics,
  SESSION_STATES,
  CONTEXT_TYPES 
} from '../Utils/aiContextManager';
import { 
  estimatePromptTokens, 
  validateTokenBudget, 
  MAX_TOKENS_MEDIUM,
  logTokenBreakdown 
} from '../Utils/tokenEstimator';
class AzureAiService {
  constructor() {
    this.backendUrl = this.getBackendUrl();
    this.isInitialized = false;
    
    // Configuration for retry logic and timeouts
    this.maxRetries = 1; // Single retry to avoid long waits
    this.baseDelay = 2000; // 2 second base delay for retries
    this.timeout = 90000; // 90 second timeout for AI processing (matches backend AZURE_AI_MAX_POLL_SECONDS)
    this.streamingTimeout = 90000; // 90 second timeout for streaming responses
    
    // Context management
    this.contextManager = aiContextManager;
    this.currentSessionId = null;
    this.performanceMetrics = {
      totalRequests: 0,
      totalTokensSaved: 0,
      averageResponseTime: 0,
      contextEstablishmentTime: 0
    };
  }

  /**
   * Get the backend URL from environment variables with validation
   * @returns {string} The validated backend URL
   * @throws {Error} If URL is missing in production or invalid
   */
  getBackendUrl() {
    const envUrl = process.env.REACT_APP_AZURE_AI_BACKEND_URL || process.env.AZURE_AI_BACKEND_URL;
    
    // In production, require the environment variable
    if (process.env.NODE_ENV === 'production' && !envUrl) {
      throw new Error(
        'AZURE_AI_BACKEND_URL environment variable is required in production. ' +
        'Please set REACT_APP_AZURE_AI_BACKEND_URL or AZURE_AI_BACKEND_URL.'
      );
    }

    // Use environment variable if available, otherwise fall back to development default
    const url = envUrl || 'https://aibackenddev.zavior.app/api/v1/chat';
    
    // Validate URL format
    if (!this.isValidUrl(url)) {
      throw new Error(
        `Invalid AZURE_AI_BACKEND_URL: "${url}". Must be a valid URL.`
      );
    }

    console.log(`🔧 Azure AI Service initialized with backend URL: ${url}`);
    return url;
  }

  /**
   * GET /health URL derived from the chat POST URL.
   * Supports legacy `/api/chat` and FastAPI `/api/v1/chat`.
   */
  getHealthCheckUrl() {
    const chat = this.backendUrl;
    if (chat.includes('/api/v1/chat')) {
      try {
        return new URL('/health', chat).href;
      } catch {
        return chat.replace(/\/api\/v1\/chat\/?$/, '') + '/health';
      }
    }
    return chat.replace('/api/chat', '/health');
  }

  /**
   * POST clear-thread URL derived from the chat POST URL.
   * Supports legacy `/api/chat` + `/api/chat/clear` and `/api/v1/chat` + `/api/v1/chat/clear`.
   */
  getClearThreadUrl() {
    const chat = this.backendUrl;
    if (chat.includes('/api/v1/chat')) {
      try {
        const u = new URL(chat);
        u.pathname = '/api/v1/chat/clear';
        return u.href;
      } catch {
        return chat.replace(/\/api\/v1\/chat\/?$/, '/api/v1/chat/clear');
      }
    }
    return chat.replace('/api/chat', '/api/chat/clear');
  }

  /**
   * Validate if the given string is a valid URL
   * @param {string} url - The URL to validate
   * @returns {boolean} True if valid URL, false otherwise
   */
  isValidUrl(url) {
    if (!url || typeof url !== 'string') {
      return false;
    }
    
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  }

  // Initialize the service
  async initialize() {
    try {
      // Test backend connectivity
      const healthUrl = this.getHealthCheckUrl();
      const response = await fetch(healthUrl);
      if (response.ok) {
        this.isInitialized = true;
        console.log('✅ Azure AI backend connected');
        return true;
      }
    } catch (error) {
      console.warn('⚠️ Backend not available:', error.message);
    }
    
    this.isInitialized = false;
    return false;
  }

  /**
   * Initialize a new AI session with control context
   * @param {string} controlId - Control identifier
   * @param {Object} controlContext - Full control context data
   * @returns {string} Session ID for the new session
   */
  initializeSession(controlId, controlContext) {
    try {
      console.log(`🆕 Initializing AI session for control: ${controlId}`);
      
      const sessionId = createAISession(controlId, controlContext);
      this.currentSessionId = sessionId;
      
      // Log context size for monitoring
      const contextTokens = estimatePromptTokens('', controlContext, []);
      console.log(`📊 Context initialized: ${contextTokens.total} tokens`);
      
      return sessionId;
    } catch (error) {
      console.error('Error initializing AI session:', error);
      throw error;
    }
  }

  /**
   * Send a context-aware message using session management
   * @param {string} userMessage - The user's message
   * @param {string} sessionId - Session ID (required for context management)
   * @param {Object} options - Additional options
   * @returns {Object} Enhanced response with session analytics
   */
  async sendContextAwareMessage(userMessage, sessionId, options = {}) {
    const startTime = Date.now();
    
    try {
      console.log(`📤 Sending context-aware message to session: ${sessionId}`);
      
      // Get session info
      const session = this.contextManager.getSession(sessionId);
      if (!session) {
        throw new Error('Session not found or expired. Please start a new conversation.');
      }
      
      // Update session state based on context status
      if (!session.contextSent) {
        updateAISessionState(sessionId, SESSION_STATES.LOADING_CONTEXT);
      }
      
      // Build optimized payload
      const payload = buildAIPayload(sessionId, userMessage, options);
      
      // Log token analysis in development
      if (process.env.NODE_ENV === 'development') {
        const tokenBreakdown = estimatePromptTokens(
          payload.message,
          payload.context,
          session.conversationHistory
        );
        logTokenBreakdown(tokenBreakdown, `Session ${sessionId}`);
        
        // Validate token budget
        const validation = validateTokenBudget(tokenBreakdown.total, MAX_TOKENS_MEDIUM);
        if (!validation.isValid) {
          console.warn('⚠️ Token budget exceeded:', validation.suggestions);
        }
      }
      
      // Send request to backend
      const response = await this.sendOptimizedRequest(payload);
      
      // Update session state on success
      if (response.success) {
        if (!session.contextSent) {
          updateAISessionState(sessionId, SESSION_STATES.CONTEXT_ESTABLISHED, {
            contextEstablishmentTime: Date.now() - startTime
          });
        }
        
        // Add to conversation history
        this.contextManager.addToHistory(
          sessionId,
          userMessage,
          response.message,
          {
            responseTime: Date.now() - startTime,
            contextType: payload.contextType,
            tokens: payload.metadata?.estimatedContextTokens || 0
          }
        );
      } else {
        updateAISessionState(sessionId, SESSION_STATES.CONTEXT_FAILED);
      }
      
      // Update performance metrics
      this.updatePerformanceMetrics(sessionId, startTime);
      
      // Add session analytics to response
      const analytics = getAISessionAnalytics(sessionId);
      
      return {
        ...response,
        sessionAnalytics: analytics,
        contextType: payload.contextType,
        isFirstMessage: payload.isFirstMessage,
        tokensSaved: analytics?.tokenSavings || 0
      };
      
    } catch (error) {
      console.error('❌ Context-aware message failed:', error);
      updateAISessionState(sessionId, SESSION_STATES.CONTEXT_FAILED);
      
      return {
        success: false,
        error: error.message,
        message: this.getContextualErrorMessage(error),
        source: 'context-error'
      };
    }
  }

  /**
   * Send optimized request to backend
   * @private
   */
  async sendOptimizedRequest(payload) {
    let lastError;
    
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          const delay = this.baseDelay * Math.pow(2, attempt - 1);
          console.log(`⏳ Retrying request in ${delay}ms (attempt ${attempt + 1}/${this.maxRetries + 1})`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        console.log(`🚀 Sending AI request (attempt ${attempt + 1})`);
        
        // Enhanced payload preview for multi-control sessions
        const payloadPreview = {
          message: payload.message?.substring(0, 100) + '...',
          contextSize: JSON.stringify(payload.context || {}).length,
          sessionId: payload.sessionId,
          contextType: payload._contextType || 'legacy'
        };

        // Add multi-control specific logging
        if (payload._isMultiControl) {
          payloadPreview.isMultiControl = true;
          payloadPreview.globalSessionId = payload._globalSessionId;
          payloadPreview.contextAction = payload._contextAction;
          payloadPreview.controlCount = payload._sessionMetadata?.controlCount || 0;
          payloadPreview.totalContextTokens = payload._sessionMetadata?.totalContextTokens || 0;
        }

        console.log('📦 Payload preview:', payloadPreview);
        
        // Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.warn(`⏰ Request timeout after ${this.timeout}ms`);
          controller.abort();
        }, this.timeout);

        const response = await fetch(this.backendUrl, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            // Add custom headers for multi-control sessions
            ...(payload._isMultiControl && {
              'X-Multi-Control-Session': payload._globalSessionId,
              'X-Context-Action': payload._contextAction,
              'X-Control-Count': payload._sessionMetadata?.controlCount?.toString() || '1'
            })
          },
          body: JSON.stringify(payload)
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          // Try to get error details from response
          let errorDetails = '';
          let errorData = null;
          try {
            const responseText = await response.text();
            errorData = JSON.parse(responseText);
            errorDetails = errorData.error || responseText;
          } catch (e) {
            // Ignore error parsing error
          }
          
          // Enhanced error logging for backend issues
          if (response.status === 500 && errorData?.error?.includes('Connection aborted')) {
            console.error('🚨 Azure AI Backend Connectivity Issue:', errorData);
            throw new Error(`Azure AI service is temporarily unavailable. The backend is having trouble connecting to Azure AI services. Please try again in a moment.`);
          }
          
          if (response.status === 500 && errorData?.error?.includes('RemoteDisconnected')) {
            console.error('🚨 Azure AI Remote Connection Lost:', errorData);
            throw new Error(`Azure AI connection was lost during processing. This may be due to a temporary service interruption. Please try again.`);
          }
          
          // Log full error details for debugging
          console.error(`Backend Error Details:`, {
            status: response.status,
            statusText: response.statusText,
            errorData: errorData,
            url: this.backendUrl
          });
          
          throw new Error(`Backend error: ${response.status} - ${response.statusText}${errorDetails ? ` - ${errorDetails}` : ''}`);
        }

        const data = await response.json();
        
        // Enhanced success logging for multi-control
        const successMessage = payload._isMultiControl 
          ? `✅ Multi-control AI response received (${payload._sessionMetadata?.controlCount} controls)`
          : '✅ Azure AI response received';
        
        console.log(successMessage);
        
        return {
          success: true,
          message: this.removeCitations(data.message),
          timestamp: data.timestamp,
          source: data.source || 'azure-ai-agent-service',
          threadId: data.threadId,
          runId: data.runId,
          // Add multi-control metadata to response
          metadata: {
            isMultiControl: payload._isMultiControl || false,
            globalSessionId: payload._globalSessionId,
            controlCount: payload._sessionMetadata?.controlCount || 1,
            contextAction: payload._contextAction
          }
        };
      
      } catch (error) {
        lastError = error;
        
        // Enhanced error logging with specific error types
        if (error.name === 'AbortError') {
          console.error(`❌ Request timed out after ${this.timeout}ms (attempt ${attempt + 1}):`, error.message);
          lastError = new Error(`Request timed out after ${this.timeout / 1000} seconds. The AI service may be experiencing high load or connectivity issues.`);
        } else if (error.message.includes('Failed to fetch')) {
          console.error(`❌ Network error (attempt ${attempt + 1}):`, error.message);
          lastError = new Error(`Network connection error. Please check your internet connection and try again.`);
        } else {
          console.error(`❌ Request attempt ${attempt + 1} failed:`, error.message);
        }
        
        // Don't retry on client errors (4xx) or if aborted
        if (error.name === 'AbortError' || (error.message.includes('Backend error: 4'))) {
          console.log('⚠️ Not retrying due to client error or abort');
          break;
        }
        
        if (attempt === this.maxRetries) {
          break;
        }
        
        // Add delay before retry
        if (attempt < this.maxRetries) {
          console.log(`⏳ Retrying in ${this.baseDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, this.baseDelay));
        }
      }
    }
    
    throw lastError;
  }

  /**
   * Update performance metrics
   * @private
   */
  updatePerformanceMetrics(sessionId, startTime) {
    const responseTime = Date.now() - startTime;
    const analytics = getAISessionAnalytics(sessionId);
    
    this.performanceMetrics.totalRequests++;
    this.performanceMetrics.totalTokensSaved += analytics?.tokenSavings || 0;
    this.performanceMetrics.averageResponseTime = 
      (this.performanceMetrics.averageResponseTime + responseTime) / 2;
  }

  /**
   * Get contextual error message based on error type
   * @private
   */
  getContextualErrorMessage(error) {
    if (error.message.includes('Session not found')) {
      return "It looks like our conversation session has expired. Let's start fresh! 🔄";
    }
    
    if (error.message.includes('Token budget exceeded')) {
      return "That's a complex question! Let me break it down into smaller parts for better analysis. 🧩";
    }
    
    if (error.message.includes('Azure AI service is temporarily unavailable')) {
      return "🤖 Azure AI is taking a quick coffee break ☕ - our backend can't reach the AI service right now. Please try again in a moment!";
    }
    
    if (error.message.includes('connection was lost during processing')) {
      return "🔗 Connection hiccup detected! The AI service disconnected mid-conversation. Let's try that again! 🔄";
    }
    
    if (error.message.includes('Request timed out')) {
      return "⏰ That's taking longer than expected! The AI service might be busy processing other requests. Please try again! ⏳";
    }
    
    if (error.message.includes('Network connection error')) {
      return "🌐 Network connection seems wobbly! Please check your internet connection and try again. 📶";
    }
    
    // Default to existing fun error messages
    const errorMessages = [
      "Halt! Zavvy's circuits are momentarily caught in a suspiciously quiet digital alley. Running a stealthy retreat… back soon! 🕶️🕵️‍♂️",
      "I'd help you right now, but I'm in the middle of convincing a firewall that I'm not a threat. Give me a sec to charm it. 🔐😉",
      "Systems check: 98% operational. That last 2%? Currently locked in a dramatic standoff with a stubborn packet. 🎭💾"
    ];
    
    return errorMessages[Math.floor(Math.random() * errorMessages.length)];
  }

  /**
   * Get session analytics for monitoring
   * @param {string} sessionId - Session identifier
   * @returns {Object} Session analytics data
   */
  getSessionAnalytics(sessionId) {
    return getAISessionAnalytics(sessionId);
  }

  /**
   * Get overall performance metrics
   * @returns {Object} Performance metrics
   */
  getPerformanceMetrics() {
    return {
      ...this.performanceMetrics,
      activeSessions: this.contextManager.getAllSessions().length,
      totalSessions: this.contextManager.sessions.size
    };
  }

  //Send message to Azure AI backend (Legacy method - maintained for backward compatibility)
  //@deprecated Use sendContextAwareMessage instead for better performance
  async sendMessage(userMessage, context = {}, sessionId = 'default') {
    try {
      console.log('🤖 Sending message to Azure AI backend...');
      
      const response = await fetch(this.backendUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: userMessage,
          context: this.enhanceContext(context),
            sessionId
          })
        });

        if (!response.ok) {
          throw new Error(`Backend error: ${response.status} - ${response.statusText}`);
        }

        const data = await response.json();
      console.log('✅ Azure AI response received');
        
        return {
          success: true,
          message: this.removeCitations(data.message),
          timestamp: data.timestamp,
        source: data.source || 'azure-ai-agent-service',
          threadId: data.threadId,
          runId: data.runId
        };
      
    } catch (error) {
      console.error('❌ Backend communication failed:', error);
      
      // Fun rotating error messages
      const errorMessages = [
        "Halt! Zavvy's circuits are momentarily caught in a suspiciously quiet digital alley. Running a stealthy retreat… back soon! 🕶️🕵️‍♂️",
        "I'd help you right now, but I'm in the middle of convincing a firewall that I'm not a threat. Give me a sec to charm it. 🔐😉",
        "Systems check: 98% operational. That last 2%? Currently locked in a dramatic standoff with a stubborn packet. 🎭💾",
        "Zavvy attempted to engage—but the digital vibes are… off. Might be a ghost protocol. Investigating quietly. 👻💻",
        "I'm experiencing a slight interference—possibly encrypted sass. While I decode it, feel free to hydrate! 🧃🧠",
        "Zavvy here—currently dodging suspicious data shadows. Can't proceed just yet, but I'll be back before you can say 'zero-day exploit.' 🔍⚡",
        "Pause in progress. Someone tripped the digital tripwire and now everything's suspiciously silent. Time to investigate ninja-style. 🥷🔍",
        "Minor disruption in Zavvy's digital dojo. The connection chi is off-balance. Restoring equilibrium… 🧘‍♂️🛰️",
        "My data senses are tingling... and not in a good way. Holding off until things stop looking phishy. 🐟🚫",
        "Zavvy is currently locked in a cybersecurity staring contest with the void. Results pending. Please stand by. 😐🕳️"
      ];
      
      // Get a random error message
      const randomMessage = errorMessages[Math.floor(Math.random() * errorMessages.length)];
      
      // Simple fallback response
        return {
          success: false,
        error: error.message,
        message: randomMessage,
          source: 'error'
        };
    }
  }

  //Enhance context with additional information
  enhanceContext(context) {
    if (!context.controlData) {
      return context;
    }

    // Add any additional context processing here
    return {
      ...context,
      enhanced: true,
      timestamp: new Date().toISOString()
    };
  }

  //Get conversation history (not implemented in backend yet)
  async getConversationHistory(sessionId = 'default') {
    // Backend doesn't support this yet, return empty
    return [];
  }

  /**
   * Clear conversation thread with timeout, retry logic, and exponential backoff
   * @param {string} sessionId - The session ID to clear (default: 'default')
   * @returns {Object} Result object with success status and details
   * @returns {boolean} returns.success - Whether the operation succeeded
   * @returns {string} [returns.sessionId] - The session ID that was cleared (on success)
   * @returns {string} [returns.error] - Error message (on failure)
   * @returns {string} [returns.lastError] - Last error message encountered (on failure)
   */
  async clearThread(sessionId = 'default') {
    let lastError;
    
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const clearUrl = this.getClearThreadUrl();
        
        // Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);
        
        const response = await fetch(clearUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId }),
          signal: controller.signal
        });
        
        // Clear timeout since request completed
        clearTimeout(timeoutId);
        
        // Check HTTP response status
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        console.log(`✅ Thread cleared successfully for session: ${sessionId}`);
        return { success: true, sessionId };
        
      } catch (error) {
        lastError = error;
        
        // Don't retry on client errors (4xx) or if aborted
        if (error.name === 'AbortError' || (error.message && error.message.includes('HTTP 4'))) {
          break;
        }
        
        // If this is the last attempt, don't wait
        if (attempt === this.maxRetries) {
          break;
        }
        
        // Calculate delay with exponential backoff
        const delay = this.baseDelay * Math.pow(2, attempt);
        console.warn(`⚠️ Clear thread attempt ${attempt + 1} failed for session ${sessionId}, retrying in ${delay}ms:`, error.message);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    // All retries exhausted or non-retryable error
    const errorMessage = `Failed to clear thread for session ${sessionId} after ${this.maxRetries + 1} attempts. Last error: ${lastError?.message || 'Unknown error'}`;
    console.error('❌', errorMessage);
    
    // Return error result instead of throwing to allow callers to handle gracefully
    return { 
      success: false, 
      error: errorMessage, 
      sessionId,
      lastError: lastError?.message 
    };
  }

  //Remove Azure OpenAI citations from response
  removeCitations(message) {
    if (!message || typeof message !== 'string') {
      return message;
    }
    
    // Remove Azure OpenAI citation patterns like 【8:0†source】, 【4†source】, etc.
    return message.replace(/【[^】]*†source】/g, '').trim();
  }

  //Check if service is available
  isAvailable() {
    return this.isInitialized;
  }

  /**
   * Configure retry and timeout settings
   * @param {Object} config - Configuration object
   * @param {number} config.maxRetries - Maximum number of retry attempts (default: 3)
   * @param {number} config.baseDelay - Base delay in milliseconds for exponential backoff (default: 1000)
   * @param {number} config.timeout - Request timeout in milliseconds (default: 10000)
   */
  configureRetrySettings(config) {
    if (config.maxRetries !== undefined) {
      this.maxRetries = Math.max(0, config.maxRetries);
    }
    if (config.baseDelay !== undefined) {
      this.baseDelay = Math.max(100, config.baseDelay);
    }
    if (config.timeout !== undefined) {
      this.timeout = Math.max(1000, config.timeout);
    }
    
    console.log(`🔧 Azure AI Service retry settings updated:`, {
      maxRetries: this.maxRetries,
      baseDelay: this.baseDelay,
      timeout: this.timeout
    });
  }

  //Check if service is already initialized (without network call)
  isAlreadyInitialized() {
    return this.isInitialized;
  }

  //Force re-initialization (useful for testing or recovery)
  async forceReinitialize() {
    this.isInitialized = false;
    return await this.initialize();
  }

  /**
   * Submit user feedback on AI responses
   * @param {Object} feedbackData - Feedback data object
   * @param {string} feedbackData.messageId - ID of the message being rated
   * @param {string} feedbackData.feedbackType - Type of feedback ('positive' or 'negative')
   * @param {string} feedbackData.sessionId - Session ID for the conversation
   * @param {string} feedbackData.controlId - Control ID for context
   * @param {Date} feedbackData.timestamp - Timestamp of the feedback
   * @returns {Promise<Object>} Result object with success status
   */
  async submitFeedback(feedbackData) {
    try {
      // Store feedback locally for analytics
      const feedbackKey = `ai_feedback_${feedbackData.messageId}`;
      const feedbackWithMetadata = {
        ...feedbackData,
        storedAt: new Date().toISOString(),
        userAgent: navigator.userAgent
      };
      
      localStorage.setItem(feedbackKey, JSON.stringify(feedbackWithMetadata));
      
      console.log(`✅ Feedback stored locally: ${feedbackData.feedbackType} for message ${feedbackData.messageId}`);
      
      // TODO: Send to backend analytics endpoint when available
      // This would integrate with your analytics pipeline
      // Example:
      // await fetch(`${this.backendUrl}/feedback`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(feedbackData)
      // });
      
      return { success: true };
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      return { success: false, error: error.message };
    }
  }
}

// Create and export singleton instance
const azureAiServiceInstance = new AzureAiService();
export default azureAiServiceInstance;